import { cn } from '@/lib/utils';
import { Children, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

interface PaneProps {
  direction: 'horizontal' | 'vertical';
  persistenceKey?: string;
  defaultSizes?: number[];
  children: ReactNode;
  className?: string;
}

function Pane({ direction, persistenceKey, defaultSizes, children, className }: PaneProps) {
  const childArray = Children.toArray(children);
  const count = childArray.length;
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef<{
    index: number;
    startPos: number;
    startSizes: number[];
  } | null>(null);

  const [sizes, setSizes] = useState<number[]>(() => {
    if (persistenceKey) {
      try {
        const stored = localStorage.getItem(persistenceKey);
        if (stored) {
          const parsed = JSON.parse(stored) as number[];
          if (parsed.length === count) return parsed;
        }
      } catch {}
    }
    return defaultSizes?.length === count ? defaultSizes : Array(count).fill(100 / count);
  });

  // Sync sizes if child count changes
  useEffect(() => {
    if (sizes.length !== count) {
      setSizes(Array(count).fill(100 / count));
    }
  }, [count, sizes.length]);

  // Persist sizes
  useEffect(() => {
    if (persistenceKey) {
      localStorage.setItem(persistenceKey, JSON.stringify(sizes));
    }
  }, [sizes, persistenceKey]);

  const isHorizontal = direction === 'horizontal';

  const onPointerDown = useCallback(
    (index: number, e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragging.current = {
        index,
        startPos: isHorizontal ? e.clientX : e.clientY,
        startSizes: [...sizes],
      };
      document.body.classList.add('select-none');
    },
    [sizes, isHorizontal],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragging.current;
      if (!drag || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const containerSize = isHorizontal ? rect.width : rect.height;
      const pos = isHorizontal ? e.clientX : e.clientY;
      const deltaPx = pos - drag.startPos;
      const deltaPct = (deltaPx / containerSize) * 100;

      const minSize = 5;
      const { index, startSizes } = drag;
      let newLeft = startSizes[index] + deltaPct;
      let newRight = startSizes[index + 1] - deltaPct;

      if (newLeft < minSize) {
        newLeft = minSize;
        newRight = startSizes[index] + startSizes[index + 1] - minSize;
      }
      if (newRight < minSize) {
        newRight = minSize;
        newLeft = startSizes[index] + startSizes[index + 1] - minSize;
      }

      setSizes((prev) => {
        const next = [...prev];
        next[index] = newLeft;
        next[index + 1] = newRight;
        return next;
      });
    },
    [isHorizontal],
  );

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    dragging.current = null;
    document.body.classList.remove('select-none');
  }, []);

  const elements: ReactNode[] = [];
  childArray.forEach((child, i) => {
    elements.push(
      <div
        key={`pane-${i}`}
        className='overflow-hidden flex flex-col'
        style={{ flexBasis: `${sizes[i]}%`, flexGrow: 0, flexShrink: 0 }}>
        {child}
      </div>,
    );

    if (i < count - 1) {
      elements.push(
        <div
          key={`divider-${i}`}
          data-slot='pane-divider'
          className={cn(
            'relative flex shrink-0 items-center justify-center',
            isHorizontal ? 'cursor-col-resize flex-col' : 'cursor-row-resize flex-row',
          )}
          style={isHorizontal ? { width: 0 } : { height: 0 }}
          onPointerDown={(e) => onPointerDown(i, e)}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}>
          {/* Visible line */}
          <div
            className={cn('bg-border pointer-events-none absolute z-10', isHorizontal ? 'h-full w-px' : 'h-px w-full')}
          />
          {/* Wider hit area */}
          <div
            className={cn(
              'hover:bg-primary/30 absolute z-20 transition-colors',
              isHorizontal ? 'h-full w-2' : 'h-2 w-full',
            )}
          />
        </div>,
      );
    }
  });

  return (
    <div
      ref={containerRef}
      data-slot='pane'
      className={cn('flex overflow-hidden flex-1', isHorizontal ? 'flex-row' : 'flex-col', className)}>
      {elements}
    </div>
  );
}

export { Pane };
export type { PaneProps };
